import { getCurrentInstance, onBeforeUnmount, ref } from 'vue';

export type SseStatus = 'connecting' | 'open' | 'polling' | 'closed';

export type SseOptions<T> = {
  url: string;
  /** Polling fallback (called every `pollIntervalMs`). */
  pollFn: () => Promise<T>;
  pollIntervalMs?: number;
  fallbackTimeoutMs?: number;
  /**
   * Mint a short-lived signed token for `?token=` query param.
   * EventSource cannot set `Authorization`, so the route accepts a
   * one-shot token bound to (user, generation) instead. Called once
   * per SSE attempt (and again if we ever reconnect).
   */
  tokenFn: () => Promise<string>;
  /** Map a parsed SSE event ({event, data}) to T. */
  parseEvent: (event: string, data: unknown) => T;
  /** Decide if T is terminal (stops streaming/polling). */
  isTerminal: (t: T) => boolean;
};

export function useSSE<T>(opts: SseOptions<T>) {
  const status = ref<SseStatus>('connecting');
  const lastEvent = ref<T | null>(null);
  const isPolling = ref(false);
  let es: EventSource | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  function close() {
    closed = true;
    status.value = 'closed';
    if (es) {
      es.close();
      es = null;
    }
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  }

  function emit(event: string, data: unknown) {
    if (closed) return;
    const t = opts.parseEvent(event, data);
    lastEvent.value = t;
    if (opts.isTerminal(t)) close();
  }

  function startPolling() {
    if (closed) return;
    if (es) {
      es.close();
      es = null;
    }
    isPolling.value = true;
    status.value = 'polling';
    const interval = opts.pollIntervalMs ?? 3000;
    const tick = async () => {
      if (closed) return;
      try {
        const t = await opts.pollFn();
        lastEvent.value = t;
        if (opts.isTerminal(t)) close();
      } catch {
        // Keep polling — transient failure shouldn't kill the page.
      }
    };
    void tick();
    pollTimer = setInterval(() => void tick(), interval);
  }

  async function startSse() {
    // Native EventSource can't pass headers; the SSE route accepts a
    // one-shot signed token via `?token=` instead.
    let token: string;
    try {
      token = await opts.tokenFn();
    } catch {
      // Couldn't mint a token — fall back to polling immediately.
      startPolling();
      return;
    }
    if (closed) return;
    try {
      const sep = opts.url.includes('?') ? '&' : '?';
      const url = `${opts.url}${sep}token=${encodeURIComponent(token)}`;
      es = new EventSource(url);
    } catch {
      startPolling();
      return;
    }
    status.value = 'connecting';

    fallbackTimer = setTimeout(() => {
      if (status.value === 'connecting') startPolling();
    }, opts.fallbackTimeoutMs ?? 3000);

    es.onopen = () => {
      if (closed) return;
      status.value = 'open';
    };
    const handle = (event: string) => (msgEvt: MessageEvent) => {
      let data: unknown;
      try {
        data = JSON.parse(msgEvt.data);
      } catch {
        data = msgEvt.data;
      }
      emit(event, data);
    };
    for (const ev of ['tick', 'done', 'failed']) {
      es.addEventListener(ev, handle(ev));
    }
    es.onerror = () => {
      if (closed) return;
      // EventSource auto-retries; if it never opened, switch to polling.
      if (status.value === 'connecting') startPolling();
    };
  }

  void startSse();
  // Auto-cleanup only when called from a component setup. Pinia stores
  // call this from non-setup contexts (e.g. inside an action), where
  // onBeforeUnmount has no instance to bind to and would warn — the
  // store handles its own lifecycle via stopWatch().
  if (getCurrentInstance()) {
    onBeforeUnmount(close);
  }

  return { status, lastEvent, isPolling, close };
}
